import { z } from 'zod';
import type { TaskEventPreferenceKey } from './enums/notifications';
import { hubTaskAliasPath } from './links';

/**
 * Task vocabulary (SPEC §6, PLAN §3.2 "Tasks and delegation", D-037, D-054, D-055, D-059, D-060).
 * These arrays are the single source of truth for the database CHECK constraints
 * (`packages/db/src/schema/tasks.ts`, `projects.ts`), the API contracts
 * (`@orbit/shared/contracts/tasks`) and `@orbit/domain/tasks`, which re-exports them (D-200).
 * Import from `@orbit/shared/tasks` (or the package root).
 */

// ---------------------------------------------------------------- the task object

/** `tasks.kind`: a plain task or a decision request (SPEC §6.3). */
export const TASK_KINDS = ['task', 'decision_request'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];
export const taskKindSchema = z.enum(TASK_KINDS);

/** `tasks.status` (SPEC §6.2). Decision requests reuse proposed / done / declined / cancelled. */
export const TASK_STATUSES = [
  'draft',
  'proposed',
  'accepted',
  'in_progress',
  'blocked',
  'done',
  'verified',
  'declined',
  'cancelled',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const taskStatusSchema = z.enum(TASK_STATUSES);

/** Accepted and later, before Done: what follow-ups, overdue and slipping look at. */
export const ACTIVE_TASK_STATUS_LIST = ['accepted', 'in_progress', 'blocked'] as const;
/** Everything that still needs someone: proposed plus the active statuses. */
export const OPEN_TASK_STATUSES = ['proposed', 'accepted', 'in_progress', 'blocked'] as const;
/** Terminal or closed for editing (PLAN §4 `409 task_closed`). */
export const CLOSED_TASK_STATUSES = ['done', 'verified', 'declined', 'cancelled'] as const;

/** Push-back sub-state of `proposed` (D-055): whose move it is. */
export const AWAITING_PARTIES = ['owner', 'delegator'] as const;
export type AwaitingParty = (typeof AWAITING_PARTIES)[number];
export const awaitingPartySchema = z.enum(AWAITING_PARTIES);

export const TASK_PRIORITIES = ['p1', 'p2', 'p3'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);

/** `tasks.visibility` (D-037). Decision requests default to `participants`. */
export const TASK_VISIBILITIES = ['department', 'participants'] as const;
export type TaskVisibility = (typeof TASK_VISIBILITIES)[number];
export const taskVisibilitySchema = z.enum(TASK_VISIBILITIES);

/** `tasks.source_type`: what produced the task (SPEC §6.1 `source_link`). */
export const TASK_SOURCE_TYPES = [
  'message',
  'thread',
  'meeting',
  'daily_report',
  'transcript',
  'weekly_review',
] as const;
export type TaskSourceType = (typeof TASK_SOURCE_TYPES)[number];
export const taskSourceTypeSchema = z.enum(TASK_SOURCE_TYPES);

/** `tasks.created_via`. */
export const TASK_CREATED_VIA = [
  'web',
  'desktop',
  'mobile',
  'quick_add',
  'slash_command',
  'message_action',
  'meeting',
  'transcript',
  'weekly_review',
  'daily_report',
  'recurrence',
  'api',
  'bot',
  'import',
] as const;
export type TaskCreatedVia = (typeof TASK_CREATED_VIA)[number];
export const taskCreatedViaSchema = z.enum(TASK_CREATED_VIA);

/** `task_events.event_type` (PLAN §3.2 `task_events`). */
export const TASK_EVENT_TYPES = [
  'created',
  'sent',
  'accepted',
  'series_accepted',
  'pushed_back',
  'pushback_answered',
  'pushback_withdrawn',
  'declined',
  'reassigned',
  'delegator_transferred',
  'started',
  'blocked',
  'unblocked',
  'completed',
  'verified',
  'reopened',
  'cancelled',
  'decided',
  'due_date_changed',
  'fields_updated',
  'watcher_added',
  'watcher_removed',
  'checklist_changed',
  'attachment_added',
  'attachment_removed',
  'kra_tagged',
  'reminder_sent',
  'escalated',
  'recurrence_generated',
] as const;
export type TaskEventType = (typeof TASK_EVENT_TYPES)[number];
export const taskEventTypeSchema = z.enum(TASK_EVENT_TYPES);

/** `task_events.actor_kind`. */
export const TASK_ACTOR_KINDS = ['user', 'system', 'bot'] as const;
export type TaskActorKind = (typeof TASK_ACTOR_KINDS)[number];

/** `task_commitments.end_reason`. */
export const COMMITMENT_END_REASONS = ['reassigned', 'cancelled', 'renegotiated'] as const;
export type CommitmentEndReason = (typeof COMMITMENT_END_REASONS)[number];

/** `task_pushbacks.status` / `resolution`. */
export const PUSHBACK_STATUSES = ['open', 'resolved'] as const;
export type PushbackStatus = (typeof PUSHBACK_STATUSES)[number];
export const PUSHBACK_RESOLUTIONS = [
  'date_agreed',
  'answered',
  'withdrawn',
  'reassigned',
  'cancelled',
] as const;
export type PushbackResolution = (typeof PUSHBACK_RESOLUTIONS)[number];

/** `task_due_date_changes.phase` / `direction` / `initiated_by` (D-059). */
export const DUE_DATE_CHANGE_PHASES = ['pre_acceptance', 'post_acceptance'] as const;
export type DueDateChangePhase = (typeof DUE_DATE_CHANGE_PHASES)[number];
export const DUE_DATE_DIRECTIONS = ['set', 'later', 'earlier', 'time_only', 'cleared'] as const;
export type DueDateDirection = (typeof DUE_DATE_DIRECTIONS)[number];
export const DUE_DATE_INITIATORS = ['owner', 'delegator', 'admin', 'system'] as const;
export type DueDateInitiator = (typeof DUE_DATE_INITIATORS)[number];

/** `task_watchers.source`. */
export const WATCHER_SOURCES = ['manual', 'self', 'mention', 'meeting', 'offboarding'] as const;
export type WatcherSource = (typeof WATCHER_SOURCES)[number];

/** `task_attachments.purpose`: `evidence` is attached when marking Done. */
export const TASK_ATTACHMENT_PURPOSES = ['reference', 'evidence'] as const;
export type TaskAttachmentPurpose = (typeof TASK_ATTACHMENT_PURPOSES)[number];

// ---------------------------------------------------------------- actions (one endpoint each)

/**
 * The state-machine actions (`@orbit/domain/tasks` `TASK_ACTIONS`, PLAN §3.2 transition tables).
 * `delete_draft` is `DELETE /tasks/:id`; the others are `POST /tasks/:id/<path>`
 * ({@link TASK_ACTION_PATHS}). `TaskDto.allowedActions` lists these values for the viewer.
 */
export const TASK_ACTIONS = [
  'send',
  'accept',
  'push_back',
  'respond_pushback',
  'decline',
  'reassign',
  'start',
  'block',
  'unblock',
  'complete',
  'verify',
  'reopen',
  'cancel',
  'delete_draft',
  'change_due_date',
  'decide',
] as const;
export type TaskAction = (typeof TASK_ACTIONS)[number];
export const taskActionSchema = z.enum(TASK_ACTIONS);

/** `POST /api/v1/tasks/:taskId/<path>` for every action except `delete_draft` (PLAN §4). */
export const TASK_ACTION_PATHS = {
  send: 'send',
  accept: 'accept',
  push_back: 'push-back',
  respond_pushback: 'push-back/respond',
  decline: 'decline',
  reassign: 'reassign',
  start: 'start',
  block: 'block',
  unblock: 'unblock',
  complete: 'complete',
  verify: 'verify',
  reopen: 'reopen',
  cancel: 'cancel',
  change_due_date: 'change-due-date',
  decide: 'decide',
} as const satisfies Record<Exclude<TaskAction, 'delete_draft'>, string>;
export type TaskActionPath = (typeof TASK_ACTION_PATHS)[keyof typeof TASK_ACTION_PATHS];

// ---------------------------------------------------------------- guard, recurrence, follow-ups

/** `task_recurrences.status`. */
export const RECURRENCE_STATUSES = ['active', 'paused', 'ended'] as const;
export type RecurrenceStatus = (typeof RECURRENCE_STATUSES)[number];
export const recurrenceStatusSchema = z.enum(RECURRENCE_STATUSES);

/** `task_recurrences.non_working_day_rule` (the calendar's `adjustToWorkingDay`). */
export const NON_WORKING_DAY_RULES = [
  'previous_working_day',
  'next_working_day',
  'skip',
  'keep',
] as const;
export type NonWorkingDayRule = (typeof NON_WORKING_DAY_RULES)[number];
export const nonWorkingDayRuleSchema = z.enum(NON_WORKING_DAY_RULES);

/** `task_followups.kind` (PLAN §3.2 follow-up ledger, D-060). */
export const FOLLOWUP_KINDS = [
  'acceptance_reminder',
  'acceptance_notify_delegator',
  'pushback_reminder',
  'due_today_nudge',
  'overdue_notice',
  'overdue_escalation_delegator',
  'overdue_escalation_manager',
  'verification_reminder',
  'decision_deadline_reminder',
  'decision_overdue_notice',
  'morning_digest',
] as const;
export type FollowupKind = (typeof FOLLOWUP_KINDS)[number];

/** `messages.metadata.source` of every DM the follow-up automation posts as the Hub user (D-060). */
export const FOLLOWUP_MESSAGE_SOURCE = 'task_followup';

/**
 * The `notification_preferences.task_events` key a follow-up DM stands for on the phone (D-582): the
 * "A task is due today" and "A task is overdue" toggles. The other kinds (the digest, acceptance and
 * verification reminders, decision reminders) have no toggle of their own.
 */
export const FOLLOWUP_EVENT_KEYS: Readonly<Partial<Record<FollowupKind, TaskEventPreferenceKey>>> =
  {
    due_today_nudge: 'task_due_today',
    overdue_notice: 'task_overdue',
    overdue_escalation_delegator: 'task_overdue',
    overdue_escalation_manager: 'task_overdue',
  };

/**
 * The `task_events` key of a message from its `metadata`: set for the follow-up DMs above, `null`
 * for every other message (which only follows the levels and mute).
 */
export function followupEventKey(
  metadata: Readonly<Record<string, unknown>> | null | undefined,
): TaskEventPreferenceKey | null {
  if (!metadata || metadata.source !== FOLLOWUP_MESSAGE_SOURCE) return null;
  const kind = metadata.followupKind;
  if (typeof kind !== 'string' || !Object.hasOwn(FOLLOWUP_EVENT_KEYS, kind)) return null;
  return FOLLOWUP_EVENT_KEYS[kind as FollowupKind] ?? null;
}

/** `task_followups.status`. */
export const FOLLOWUP_STATUSES = ['claimed', 'sent', 'skipped', 'failed'] as const;
export type FollowupStatus = (typeof FOLLOWUP_STATUSES)[number];

/** `task_followups.skip_reason`. */
export const FOLLOWUP_SKIP_REASONS = [
  'empty_digest',
  'recipient_inactive',
  'recipient_on_leave',
  'task_state_changed',
  'manager_is_delegator',
  'no_manager',
  'stale',
] as const;
export type FollowupSkipReason = (typeof FOLLOWUP_SKIP_REASONS)[number];

// ---------------------------------------------------------------- projects (PLAN §3.2)

export const PROJECT_STATUSES = [
  'active',
  'on_hold',
  'completed',
  'cancelled',
  'archived',
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const projectStatusSchema = z.enum(PROJECT_STATUSES);

/** `projects.visibility`: guests never see `workspace` projects. */
export const PROJECT_VISIBILITIES = ['workspace', 'members'] as const;
export type ProjectVisibility = (typeof PROJECT_VISIBILITIES)[number];
export const projectVisibilitySchema = z.enum(PROJECT_VISIBILITIES);

export const PROJECT_MEMBER_ROLES = ['lead', 'member', 'viewer'] as const;
export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number];
export const projectMemberRoleSchema = z.enum(PROJECT_MEMBER_ROLES);

export const MILESTONE_STATUSES = ['open', 'done'] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];
export const milestoneStatusSchema = z.enum(MILESTONE_STATUSES);

// ---------------------------------------------------------------- sensitive grants (D-036)

/**
 * `member_grants.grant` (PLAN §3.1 `member_grants`, Phase 4 table): `cockpit` lets a selected
 * manager read `department`-visible tasks company-wide (task visibility rule 8, D-037); the pay
 * grants are used from Phase 8. Guests never hold grants.
 */
export const MEMBER_GRANTS = [
  'cockpit',
  'hr_compensation',
  'payroll_export',
  'pay_approver',
] as const;
export type MemberGrant = (typeof MEMBER_GRANTS)[number];

// ---------------------------------------------------------------- limits and numbers

/**
 * Task limits the server enforces (PLAN §3.2, §4; the state machine's `TASK_LIMITS` for the
 * transition inputs). Clients use them to warn early.
 */
export const TASK_FIELD_LIMITS = Object.freeze({
  titleMax: 300,
  /** `description_text` (plain projection of the TipTap document). */
  descriptionTextMax: 20_000,
  definitionOfDoneMin: 3,
  definitionOfDoneMax: 5_000,
  reasonMax: 1_000,
  /** Post-acceptance date-change reasons and reopen comments. */
  longReasonMin: 3,
  noteMax: 2_000,
  /** Checklist items per task (`409 checklist_limit`, PLAN §4). */
  checklistMax: 50,
  checklistTitleMax: 200,
  /** `POST /tasks/bulk`. */
  bulkMax: 100,
  watchersMax: 50,
  attachmentsMax: 20,
  /** Decision requests (SPEC §6.3). */
  decisionOptionsMin: 2,
  decisionOptionsMax: 6,
  decisionContextMin: 10,
  decisionContextMax: 10_000,
  decisionRecommendationMin: 3,
  decisionRecommendationMax: 5_000,
  decisionOptionLabelMax: 200,
  decisionOptionDetailsMax: 5_000,
  /** `POST /tasks/parse` and `/task` text. */
  quickAddMax: 500,
  /** Walk limit for `blocked_by_task_id` cycles. */
  blockedByDepthMax: 20,
});

/** `T-1042` (the per-workspace number, D-003). */
export function formatTaskNumber(n: number): string {
  return `T-${n}`;
}

/**
 * The web deep link of a task: its detail panel on the Tasks page (`/tasks?task=T-1042`). The
 * format lives in the shared link map (`hubTaskAliasPath`, D-511), which parses it back to the
 * task route, so the builder and the parser cannot drift.
 */
export function taskDeepLink(n: number): string {
  return hubTaskAliasPath(n);
}

/** `T-1042` / `t-1042` / `1042` → 1042; anything else → null. Task paths accept UUID or number. */
export function parseTaskNumber(value: string): number | null {
  const match = /^(?:[Tt]-)?(\d{1,9})$/.exec(value.trim());
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** UI labels; decision requests name their statuses differently (PLAN §3.2). */
export function taskStatusLabel(kind: TaskKind, status: TaskStatus): string {
  if (kind === 'decision_request') {
    if (status === 'proposed') return 'Awaiting decision';
    if (status === 'done') return 'Decided';
  }
  return TASK_STATUS_LABELS[status];
}

export const TASK_STATUS_LABELS: Readonly<Record<TaskStatus, string>> = Object.freeze({
  draft: 'Draft',
  proposed: 'Proposed',
  accepted: 'Accepted',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  verified: 'Verified',
  declined: 'Declined',
  cancelled: 'Cancelled',
});
